/* ============================================================================
 * Wicked Clothes — collection grid ENDLESS SCROLL (SHEIN-style load-more).
 * ----------------------------------------------------------------------------
 * Page 1 renders server-side (promo/trust card + 24 products). As the shopper
 * nears the bottom, this appends pages 2+ from the SAME section via the Section
 * Rendering API (?section_id=<grid>&page=N) — reusing the real card markup so
 * appended cards are byte-identical to page-1 cards (quick-add, size data,
 * ratings). On page>1 the section returns product cards only (promo, review
 * strip, sort + facets are gated to page 1 in main-collection-product-grid).
 *
 * Trigger = a throttled SCROLL-POSITION listener on the window (distance from
 * the bottom), NOT an IntersectionObserver — the same fast-fling-immune
 * approach we settled on for the cart drawer. A fill-loop keeps loading until
 * the viewport (+ lead margin) is covered, so a tall screen or a fast fling
 * never dead-ends.
 *
 * Dedup is client-side by the handle on each <li data-wc-rec-handle>, shared
 * with the size filter (wc-size-filter.js) so the two never double-add. The
 * loader PAUSES while a size filter is active (the filter loads the whole
 * collection itself), and current sort / Shopify-facet params are kept on every
 * fetch so appended pages continue the same ordering as page 1.
 *
 * RE-INIT ON GRID SWAP: Dawn's native facets/sort replace #ProductGridContainer's
 * innerHTML (a brand-new #product-grid node). We watch that container and reset
 * our state onto the fresh grid — otherwise appends would go to a detached node
 * and endless scroll would silently die after the first sort/filter.
 *
 * SHEIN-style "go back where you left off": most back-navs restore from bfcache
 * with the whole grid + scroll intact (this IIFE doesn't even re-run). For a
 * COLD back-nav (bfcache miss) we persist the deepest page reached; on return,
 * if wc-scroll-restore.js has a pending scroll position for this URL, we
 * fast-forward those pages immediately and its retry chain lands the user.
 * ========================================================================== */
(function () {
  'use strict';

  var container = document.getElementById('ProductGridContainer');
  if (!container) return;

  var NEAR_PX = 2000;   // floor for the prefetch lead; the real lead scales with the viewport (see nearBottom)
  var MAX_PAGES = 40;   // backstop; the catalog is ~12 pages of 24 — generous slack
  var THROTTLE = 90;    // ms between scroll-handler runs (snappier detection on fast scrolls)

  var PATH = location.pathname + location.search;
  var PAGE_KEY = 'wc-col-page:' + PATH;   // deepest page reached (our own key)
  var SCROLL_KEY = 'wc-scroll:' + PATH;   // wc-scroll-restore.js's key (read-only)

  // ---- per-grid state (reset whenever Dawn swaps in a fresh grid) ----------
  var grid, sectionId, shown, page, loading, done, emptyStreak, deepest;

  function throttle(fn, ms) {
    var last = 0, timer;
    return function () {
      var now = Date.now(), wait = ms - (now - last);
      if (wait <= 0) { last = now; fn(); }
      else { clearTimeout(timer); timer = setTimeout(function () { last = Date.now(); fn(); }, wait); }
    };
  }

  // A size filter (wc-size-filter.js) owns the full collection load while
  // active — pause so we don't append unfiltered cards over a filtered view.
  function sizeFilterActive() {
    if (document.querySelector('.wc-size-filter__btn.is-active')) return true;
    try { return !!new URLSearchParams(location.search).get('wc-size'); } catch (e) { return false; }
  }

  function hidePager() {
    // Inline display:none so the size filter's `pagination.hidden = false`
    // (on filter-clear) can't bring the no-JS pager back.
    var p = document.querySelector('.pagination-wrapper');
    if (p) p.style.display = 'none';
  }

  function readHasNext(root) {
    var f = root.querySelector('[data-wc-col-hasnext]');
    return !!f && f.getAttribute('data-wc-col-hasnext') === '1';
  }

  function persist() {
    try { sessionStorage.setItem(PAGE_KEY, String(deepest)); } catch (e) {}
  }

  function fetchPage(p) {
    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { params = new URLSearchParams(); }
    params.set('section_id', sectionId);
    params.set('page', String(p));
    params.delete('wc-size');             // client-only param; the server ignores it anyway
    return fetch(location.pathname + '?' + params.toString(), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .catch(function () { return ''; });
  }

  // Parse a fetched page, append its new product cards. Returns {appended, hasNext}.
  function ingest(html) {
    if (!html) return { appended: 0, hasNext: false };
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var lis = doc.querySelectorAll('#product-grid li.grid__item[data-wc-rec-handle]');
    var appended = 0;
    lis.forEach(function (li) {
      var h = li.getAttribute('data-wc-rec-handle');
      if (!h || shown[h]) return;         // client-side dedup (shared w/ size filter)
      shown[h] = true;
      grid.appendChild(document.importNode(li, true));
      appended++;
    });
    // Re-evaluate quick-add size treatment + low-stock badges on the new cards.
    if (appended) document.dispatchEvent(new CustomEvent('wc:quickadd-refresh'));
    return { appended: appended, hasNext: readHasNext(doc) };
  }

  function docHeight() {
    return Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
  }
  function nearBottom() {
    // Prefetch ~2 viewport-heights before the bottom (min NEAR_PX) so the next
    // page fetches + renders BEFORE the shopper reaches it: covers fast scrolls
    // and slower mobile networks. A fixed 1200px fired too late, most noticeably
    // on the artist collections people scroll through fastest.
    var lead = Math.max(NEAR_PX, window.innerHeight * 2);
    return (docHeight() - (window.scrollY || window.pageYOffset || 0) - window.innerHeight) < lead;
  }

  function load() {
    if (loading || done || !grid || sizeFilterActive()) return;
    loading = true;
    var p = page;
    fetchPage(p).then(function (html) {
      loading = false;
      var res = ingest(html);
      page++;
      if (p > deepest) { deepest = p; persist(); }
      emptyStreak = res.appended === 0 ? emptyStreak + 1 : 0;
      if (!res.hasNext || page > MAX_PAGES || emptyStreak >= 2) { done = true; return; }
      // Fill-loop: a dup-heavy batch (or a tall viewport) may not have pushed the
      // bottom past the lead margin — keep loading until it does (guards bound it).
      if (nearBottom()) load();
    });
  }

  function maybeLoad() {
    if (loading || done || !grid || sizeFilterActive()) return;
    if (nearBottom()) load();
  }

  // (Re)initialise our state onto a grid node — page 1, or a fresh grid that
  // Dawn's facets/sort just swapped in.
  function bind(gridEl) {
    grid = gridEl;
    sectionId = grid.getAttribute('data-id');
    shown = {};
    grid.querySelectorAll('[data-wc-rec-handle]').forEach(function (li) {
      var h = li.getAttribute('data-wc-rec-handle');
      if (h) shown[h] = true;
    });
    page = 2;
    loading = false;
    emptyStreak = 0;
    deepest = 1;
    done = !sectionId || !readHasNext(document);
    hidePager();
  }

  // ---- SHEIN-style cold-back-nav restore -----------------------------------
  function pendingScroll() {
    try {
      var y = parseInt(sessionStorage.getItem(SCROLL_KEY), 10);
      return (!isNaN(y) && y > 0) ? y : 0;
    } catch (e) { return 0; }
  }
  function savedPage() {
    try {
      var n = parseInt(sessionStorage.getItem(PAGE_KEY), 10);
      return (!isNaN(n) && n > 1) ? n : 0;
    } catch (e) { return 0; }
  }

  // Fetch pages 2..toPage in parallel, append in order, then resume normal loading.
  function fastForward(toPage, next) {
    var jobs = [];
    for (var p = 2; p <= toPage; p++) jobs.push(fetchPage(p));
    Promise.all(jobs).then(function (htmls) {
      var hasNext = true;
      htmls.forEach(function (html) { hasNext = ingest(html).hasNext; });
      page = toPage + 1;
      deepest = toPage;
      if (!hasNext) done = true;
      if (next) next();
    });
  }

  // ---- first run -----------------------------------------------------------
  var first = document.getElementById('product-grid');
  if (!first) return;
  bind(first);

  var restoreTo = savedPage();
  if (restoreTo && pendingScroll() && !sizeFilterActive() && !done) {
    // Cold restore: rebuild the grid depth, then wc-scroll-restore's retry
    // chain (already running) scrolls the user back to where they were.
    try { sessionStorage.removeItem(PAGE_KEY); } catch (e) {}
    fastForward(restoreTo, maybeLoad);
  } else {
    try { sessionStorage.removeItem(PAGE_KEY); } catch (e) {}
    maybeLoad();   // cover the first viewport if page 1 didn't fill it
  }

  // Window scroll/resize drive loading for the page lifetime. We never detach
  // them — `done` gates the work — so a later grid swap can revive loading.
  var onScroll = throttle(maybeLoad, THROTTLE);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  // Dawn's facets/sort replace #ProductGridContainer's innerHTML → a new
  // #product-grid. Re-bind onto it. Our own appends keep the SAME node, so they
  // don't trigger a re-bind (guard: only react when the node identity changes).
  new MutationObserver(function () {
    var current = document.getElementById('product-grid');
    if (current && current !== grid) { bind(current); maybeLoad(); }
  }).observe(container, { childList: true, subtree: true });

  // Persist the deepest page on the way out — a cold back-nav reads it above.
  window.addEventListener('pagehide', persist);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') persist();
  });
})();
